import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';
import InvoiceModal from './InvoiceModal';
import TimeInvoiceModal from './TimeInvoiceModal';

const { FiPlus, FiDollarSign, FiEye, FiEdit2, FiSend, FiDownload, FiClock } = FiIcons;

const Invoices = () => {
  const { invoices, updateInvoice, addInvoice, updateTask } = useData();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTimeModalOpen, setIsTimeModalOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'text-green-600 bg-green-100';
      case 'Sent': return 'text-blue-600 bg-blue-100';
      case 'Draft': return 'text-gray-600 bg-gray-100';
      case 'Overdue': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const handleSendInvoice = (invoice) => {
    updateInvoice(invoice.id, { status: 'Sent', sentAt: new Date() });
  };

  const handleCreateTimeInvoice = (invoiceData, selectedTaskIds) => {
    // Create the invoice
    addInvoice(invoiceData);
    
    // Mark tasks as invoiced
    selectedTaskIds.forEach(taskId => {
      updateTask(taskId, { invoiced: true });
    });

    setIsTimeModalOpen(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-800">Invoices</h1>
        <div className="flex space-x-3">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setIsTimeModalOpen(true)}
            className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-700 transition-colors"
          >
            <SafeIcon icon={FiClock} />
            <span>Invoice Time</span>
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => {
              setEditingInvoice(null);
              setIsModalOpen(true);
            }}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-blue-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} />
            <span>New Invoice</span>
          </motion.button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <AnimatePresence>
          {invoices.map((invoice) => (
            <motion.div
              key={invoice.id}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              whileHover={{ y: -5 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    invoice.timeTracked ? 'bg-purple-100' : 'bg-green-100'
                  }`}>
                    <SafeIcon 
                      icon={invoice.timeTracked ? FiClock : FiDollarSign} 
                      className={`text-lg ${
                        invoice.timeTracked ? 'text-purple-600' : 'text-green-600'
                      }`} 
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800">Invoice #{invoice.number}</h3>
                    <p className="text-sm text-gray-500">{invoice.client}</p>
                    {invoice.timeTracked && (
                      <p className="text-xs text-purple-600 font-medium">Time-based</p>
                    )}
                  </div>
                </div>
                <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(invoice.status)}`}>
                  {invoice.status}
                </span>
              </div>

              <div className="space-y-2 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-semibold text-gray-800">${invoice.total?.toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Due Date:</span>
                  <span className="text-gray-600">{invoice.dueDate?.toLocaleDateString()}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">Created:</span>
                  <span className="text-gray-600">{invoice.createdAt?.toLocaleDateString()}</span>
                </div>
                {invoice.timeTracked && invoice.trackedTasks && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Tasks:</span>
                    <span className="text-purple-600 font-medium">{invoice.trackedTasks.length} tracked</span>
                  </div>
                )}
              </div>

              {/* Time tracking details */}
              {invoice.timeTracked && (
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="text-xs text-purple-700 font-medium mb-1">
                    Time-Based Invoice
                  </div>
                  <div className="text-xs text-purple-600">
                    Generated from tracked time entries
                  </div>
                </div>
              )}

              <div className="flex space-x-2">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg text-sm hover:bg-gray-200 transition-colors flex items-center justify-center space-x-1"
                >
                  <SafeIcon icon={FiEye} className="text-xs" />
                  <span>View</span>
                </motion.button>
                
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => {
                    setEditingInvoice(invoice);
                    setIsModalOpen(true);
                  }}
                  className="flex-1 bg-blue-100 text-blue-700 py-2 px-3 rounded-lg text-sm hover:bg-blue-200 transition-colors flex items-center justify-center space-x-1"
                >
                  <SafeIcon icon={FiEdit2} className="text-xs" />
                  <span>Edit</span>
                </motion.button>
                
                {invoice.status === 'Draft' && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => handleSendInvoice(invoice)}
                    className="flex-1 bg-green-100 text-green-700 py-2 px-3 rounded-lg text-sm hover:bg-green-200 transition-colors flex items-center justify-center space-x-1"
                  >
                    <SafeIcon icon={FiSend} className="text-xs" />
                    <span>Send</span>
                  </motion.button>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {invoices.length === 0 && (
        <div className="text-center py-12">
          <SafeIcon icon={FiDollarSign} className="text-6xl text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No invoices yet. Create your first invoice to get started.</p>
          <div className="flex justify-center space-x-4">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsTimeModalOpen(true)}
              className="bg-green-100 text-green-700 px-4 py-2 rounded-lg hover:bg-green-200 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiClock} />
              <span>Invoice Tracked Time</span>
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setIsModalOpen(true)}
              className="bg-blue-100 text-blue-700 px-4 py-2 rounded-lg hover:bg-blue-200 transition-colors flex items-center space-x-2"
            >
              <SafeIcon icon={FiPlus} />
              <span>Create Manual Invoice</span>
            </motion.button>
          </div>
        </div>
      )}

      <InvoiceModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingInvoice(null);
        }}
        invoice={editingInvoice}
      />

      <TimeInvoiceModal
        isOpen={isTimeModalOpen}
        onClose={() => setIsTimeModalOpen(false)}
        onCreateInvoice={handleCreateTimeInvoice}
      />
    </motion.div>
  );
};

export default Invoices;